import {
  DbDocumentsApi,
  DocumentFilter,
  DocumentFiltersOperator,
  ExportCollectionStatusData,
  ExportStatus,
} from 'src/api-new/bifrost';
import { downloadBlobFile } from 'src/utils/files/downloadBlobFile';

type OnProgressFn = (event: { message: string; type: 'message' | 'progress' }) => void;

export class DocumentsExportService {
  private dbDocumentsApi: DbDocumentsApi;

  constructor(dbDocumentsApi: DbDocumentsApi) {
    this.dbDocumentsApi = dbDocumentsApi;
  }

  async export({
    instanceId,
    collectionName,
    filters,
    filterOperator,
    onProgress,
  }: {
    instanceId: string;
    collectionName: string;
    filters: DocumentFilter[];
    filterOperator: DocumentFiltersOperator | undefined;
    onProgress: OnProgressFn;
  }) {
    const { data } = await this.dbDocumentsApi.exportDocuments({
      documentsQueryExport: {
        instance_id: instanceId,
        collection_name: collectionName,
        filters,
        filters_operator: filterOperator,
      },
    });

    const status = await this.longPollingExportStatus({
      fileNameKey: data.export_file_key,
      timeout: 1000,
      onProgress,
    });
    if (status.status === ExportStatus.Finished) {
      const response = await fetch(status.presigned_url!);
      const fileBlob = await response.blob();
      downloadBlobFile(fileBlob, data.export_file_key.split('/').pop()!);
      onProgress({ message: 'Export complete', type: 'message' });
    } else if (status.status === ExportStatus.Failed) {
      throw new Error(`Export failed with status: ${JSON.stringify(status)}`);
    }
  }

  private async longPollingExportStatus({
    fileNameKey,
    timeout,
    onProgress,
    attempt = 0,
  }: {
    fileNameKey: string;
    timeout: number;
    onProgress: OnProgressFn;
    attempt?: number;
  }): Promise<ExportCollectionStatusData> {
    return await new Promise((resolve) => {
      setTimeout(
        async () => {
          const response = await this.dbDocumentsApi.exportDocumentsByFileNameKey({ fileNameKey }).catch((e) => {
            return e;
          });
          const exportStatus = response.data.status;

          if ([ExportStatus.Finished, ExportStatus.Failed].includes(exportStatus)) {
            resolve(response.data);
          } else {
            if (exportStatus === ExportStatus.Processing) {
              onProgress({ message: `Exporting... Please don't start new export!`, type: 'progress' });
            }
            const status = await this.longPollingExportStatus({
              fileNameKey,
              timeout,
              onProgress,
              attempt: attempt + 1,
            });
            resolve(status);
          }
        },
        timeout * Math.min(attempt, 10),
      );
    });
  }
}
